import { LinkButton } from "@bitcart/ui-kit/components"
import { cn } from "@bitcart/ui-kit/utils"
import { Link } from "@bitcart/vike-kit/navigation"
import { Trans, useLingui } from "@lingui/react/macro"
import { ArrowRight, Coins, Rocket, Shield, Zap } from "lucide-react"

import { PROJECT_CANONICAL_NAME } from "@/common/constants"

import { Announcement } from "./announcement"

const FEATURE_HIGHLIGHT_CONTAINER_CLASS = `gap-2 sm:gap-3 p-3 lg:p-4 max-w-74 sm:w-74 lg:w-a rounded-lg
border-primary/50 bg-secondary/60 flex items-center justify-center border-2`

export const Hero: React.FC = () => {
  const { t } = useLingui()

  return (
    <section
      aria-labelledby="hero-heading"
      className={cn(`
        from-accent/50 via-card to-accent pb-8
        md:py-16 md:mt--16
        max-md:pt-4
        dark:from-background dark:via-background dark:to-card
        relative flex min-h-screen items-center justify-center overflow-hidden bg-linear-to-br
      `)}
    >
      <div aria-hidden="true" className="inset-0 absolute overflow-hidden">
        <div
          className={cn(`
            -top-40 -right-40 w-80 h-80 from-primary/15 to-primary/20 blur-3xl absolute
            rounded-full bg-linear-to-r
          `)}
        ></div>

        <div
          className={cn(`
            -bottom-40 -left-40 w-80 h-80 from-primary/20 to-primary/15 blur-3xl absolute
            rounded-full bg-linear-to-r
          `)}
        ></div>
      </div>

      <div className="max-w-7xl px-4 md:px-6 lg:px-8 relative mx-auto w-full text-center">
        <Announcement className="mb-8 md:mb-12" />

        <h1 id="hero-heading" className="text-6xl md:text-7xl lg:text-8xl font-bold mb-4 md:mb-7">
          <span
            className={cn(`
              from-foreground via-accent-foreground to-primary/80
              dark:via-primary-text dark:to-accent-foreground
              bg-linear-to-r bg-clip-text text-transparent
            `)}
          >
            {PROJECT_CANONICAL_NAME}
          </span>
        </h1>

        <p
          className={cn(`
            text-lg
            md:text-xl
            lg:text-2xl
            text-muted-foreground mb-3
            md:mb-4
            max-w-4xl leading-relaxed px-2 px-4 mx-auto
          `)}
        >
          <Trans>
            Start Accepting Crypto Payments With{" "}
            <span className="font-semibold text-primary-text text-nowrap">0% Fees</span> &{" "}
            <span className="font-semibold text-primary-text text-nowrap">No Third-party</span>
          </Trans>
        </p>

        <div className="gap-6 md:gap-8 mb-6 sm:mb-10 md:mb-12 flex flex-col">
          <p className="md:text-lg max-w-3xl px-2 md:px-4 text-muted-foreground mx-auto">
            {t`Self-hosted, open-source cryptocurrency payment processor. Secure, private, censorship-resistant and free.`}
          </p>

          <p className="text-muted-foreground md:text-lg max-w-3xl px-2 md:px-4 mx-auto">
            <Trans>
              💰 Accept payments in{" "}
              <Link
                href="/coins"
                className={cn(`
                  font-bold decoration-primary/50 px-1 py-0.5 rounded-xs -mx-1 -my-0.5 text-primary-text
                  hover:text-accent-foreground hover:decoration-primary hover:shadow-lg
                  hover:bg-accent
                  dark:hover:text-foreground
                  underline transition-all duration-200
                `)}
              >
                50+ cryptocurrencies including Bitcoin, Ethereum, Tron, Monero & any other popular
                coin
              </Link>{" "}
              — No fees, no middleman, YOUR coins!
            </Trans>
          </p>
        </div>

        <div
          className={cn(`
            gap-4 mb-6
            sm:mb-10
            md:mb-12
            max-sm:flex-col
            flex flex-row items-center justify-center
          `)}
        >
          <LinkButton
            href="https://admin.bitcart.ai"
            expandOnHover
            size="xl"
            className={cn(`
              group elevation-3 bg-initial from-primary to-primary/80
              hover:bg-initial hover:from-primary/80 hover:to-primary/100
              max-sm:w-full
              bg-linear-to-r
            `)}
          >
            <span>{t`Live Demo`}</span>

            <ArrowRight className="group-hover:translate-x-1 transition-transform" />
          </LinkButton>

          <LinkButton
            href="https://docs.bitcart.ai"
            expandOnHover
            size="xl"
            variant="outline"
            className={cn(`group max-sm:w-full text-primary-text bg-background border-primary`)}
          >
            <span>{t`View User Guide`}</span>

            <ArrowRight className="group-hover:translate-x-1 transition-transform" />
          </LinkButton>
        </div>

        {/* Feature highlights */}
        <div
          className="
            lg:grid-cols-4
            sm:w-fit
            lg:w-a
            gap-4
            md:gap-6
            max-w-5xl
            md:px-4
            mx-auto grid grid-cols-2
          "
        >
          {[
            { id: 1, Icon: Shield, title: t`Non-custodial` },
            { id: 2, Icon: Zap, title: t`Zero Fees` },
            { id: 3, Icon: Rocket, title: t`Self-hosted` },
          ].map(({ id, Icon, title }) => (
            <div key={id} className={FEATURE_HIGHLIGHT_CONTAINER_CLASS}>
              <Icon className="size-5 md:size-8 text-primary-text shrink-0" />

              <span
                className={cn(
                  `font-medium text-secondary-foreground text-xs sm:text-sm md:text-base`,
                )}
              >
                {title}
              </span>
            </div>
          ))}

          <Link
            href="/coins"
            className={cn(
              `group hover:bg-secondary/80 hover:border-primary transition-all duration-200`,

              FEATURE_HIGHLIGHT_CONTAINER_CLASS,
            )}
          >
            <Coins
              className={cn(`
                size-5
                md:size-8
                text-primary-text shrink-0 transition-transform
                group-hover:scale-110
              `)}
            />

            <span
              className={cn(`
                font-medium text-secondary-foreground text-xs
                sm:text-sm
                md:text-base
                group-hover:text-primary-text
                transition-colors
              `)}
            >
              {`50+ ${t`Coins`}`}
            </span>
          </Link>
        </div>
      </div>
    </section>
  )
}
